import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  SaveOutlined,
  ArrowBackOutlined,
  ExpandMore,
  Shield,
  Gavel,
  AccountBalance,
  School,
  Receipt,
  Info,
} from '@mui/icons-material';
import legalInfoService from '../services/legalInfoService';
import { LegalInfo, LegalInfoDTO, FORMES_JURIDIQUES } from '../types/legalInfo.types';

const LegalInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<LegalInfoDTO>({
    // Assurance Décennale
    assuranceNom: '',
    assuranceNumeroPolice: '',
    assuranceCouvertureGeographique: 'France Métropolitaine',
    assuranceDateValidite: '',
    assuranceMontantGarantie: 500000,

    // RC Professionnelle
    rcProAssuranceNom: '',
    rcProNumeroPolice: '',
    rcProDateValidite: '',

    // Identification juridique
    formeJuridique: '',
    capitalSocial: '',
    rcs: '',
    rm: '',

    // Conditions de paiement
    delaiPaiementJours: 30,
    tauxPenalitesRetard: 12.0,
    indemniteForfaitaireRecouvrement: 40.0,
    tauxEscompte: 0,
    delaiEscompteJours: 0,

    // Coordonnées bancaires
    iban: '',
    bic: '',
    nomBanque: '',

    // Qualifications
    qualificationProfessionnelle: '',
    mediateurConsommation: 'CNPM - Médiation de la consommation',
    mediateurSiteWeb: 'https://www.cnpm-mediation-consommation.eu',

    // TVA spécifique
    franchiseTva: false,
    agrementTvaReduite: '',
  });

  useEffect(() => {
    loadLegalInfo();
  }, []);

  const loadLegalInfo = async () => {
    try {
      setLoading(true);
      const data = await legalInfoService.getMyLegalInfo();
      if (data) {
        setFormData({
          assuranceNom: data.assuranceNom || '',
          assuranceNumeroPolice: data.assuranceNumeroPolice || '',
          assuranceCouvertureGeographique: data.assuranceCouvertureGeographique || 'France Métropolitaine',
          assuranceDateValidite: data.assuranceDateValidite || '',
          assuranceMontantGarantie: data.assuranceMontantGarantie || 500000,
          rcProAssuranceNom: data.rcProAssuranceNom || '',
          rcProNumeroPolice: data.rcProNumeroPolice || '',
          rcProDateValidite: data.rcProDateValidite || '',
          formeJuridique: data.formeJuridique || '',
          capitalSocial: data.capitalSocial || '',
          rcs: data.rcs || '',
          rm: data.rm || '',
          delaiPaiementJours: data.delaiPaiementJours || 30,
          tauxPenalitesRetard: data.tauxPenalitesRetard || 12.0,
          indemniteForfaitaireRecouvrement: data.indemniteForfaitaireRecouvrement || 40.0,
          tauxEscompte: data.tauxEscompte || 0,
          delaiEscompteJours: data.delaiEscompteJours || 0,
          iban: data.iban || '',
          bic: data.bic || '',
          nomBanque: data.nomBanque || '',
          qualificationProfessionnelle: data.qualificationProfessionnelle || '',
          mediateurConsommation: data.mediateurConsommation || 'CNPM - Médiation de la consommation',
          mediateurSiteWeb: data.mediateurSiteWeb || 'https://www.cnpm-mediation-consommation.eu',
          franchiseTva: data.franchiseTva || false,
          agrementTvaReduite: data.agrementTvaReduite || '',
        });
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement:', err);
      setError('Erreur lors du chargement des informations légales');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof LegalInfoDTO) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.type === 'checkbox'
      ? (event.target as HTMLInputElement).checked
      : event.target.value;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(false);
    setSaving(true);

    try {
      // Validation IBAN si renseigné
      if (formData.iban && formData.iban !== '' && !legalInfoService.validateIBAN(formData.iban)) {
        setError('IBAN invalide. Format attendu : FR76 1234 5678 9012 3456 7890 123');
        setSaving(false);
        return;
      }

      // Validation BIC si renseigné
      if (formData.bic && formData.bic !== '' && !legalInfoService.validateBIC(formData.bic)) {
        setError('BIC invalide. Format attendu : BNPAFRPPXXX (8 ou 11 caractères)');
        setSaving(false);
        return;
      }

      await legalInfoService.updateMyLegalInfo(formData);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
    } catch (err: any) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde des informations légales');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isComplete = legalInfoService.isComplete(formData as LegalInfo);
  const missingFields = legalInfoService.getMissingFields(formData as LegalInfo);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#333' }}>
          Informations Légales
        </Typography>
        <Button
          startIcon={<ArrowBackOutlined />}
          onClick={() => navigate('/dashboard')}
          variant="outlined"
        >
          Retour
        </Button>
      </Box>

      {/* Alerte de complétude */}
      {!isComplete && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Informations obligatoires manquantes :
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {missingFields.map((field) => (
              <li key={field}>{field}</li>
            ))}
          </ul>
        </Alert>
      )}

      {isComplete && (
        <Alert severity="success" icon={<Shield />} sx={{ mb: 3 }}>
          Toutes les informations légales obligatoires sont renseignées. Vos factures sont conformes à la législation française.
        </Alert>
      )}

      {/* Info Block */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#E3F2FD' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'start' }}>
          <Info color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Conformité Légale des Factures
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ces informations sont obligatoires pour la conformité de vos factures avec la législation française
              (Loi n° 78-12, Art. L.441-6 du Code de Commerce, Directive 2013/11/UE).
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Informations légales enregistrées avec succès !
        </Alert>
      )}

      {/* Formulaire */}
      <form onSubmit={handleSubmit}>
        {/* Section 1: Assurance Décennale */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Shield color="error" />
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Assurance Décennale
                </Typography>
                <Chip label="OBLIGATOIRE" color="error" size="small" sx={{ mt: 0.5 }} />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nom de l'assurance *"
                  value={formData.assuranceNom}
                  onChange={handleChange('assuranceNom')}
                  placeholder="Ex: AXA France IARD"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Numéro de police *"
                  value={formData.assuranceNumeroPolice}
                  onChange={handleChange('assuranceNumeroPolice')}
                  placeholder="Ex: POL-2025-123456"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Couverture géographique *"
                  value={formData.assuranceCouvertureGeographique}
                  onChange={handleChange('assuranceCouvertureGeographique')}
                  placeholder="Ex: France Métropolitaine"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date de validité *"
                  type="date"
                  value={formData.assuranceDateValidite}
                  onChange={handleChange('assuranceDateValidite')}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Montant de garantie (€)"
                  type="number"
                  value={formData.assuranceMontantGarantie}
                  onChange={handleChange('assuranceMontantGarantie')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Section 2: RC Professionnelle */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Shield color="primary" />
              <Typography variant="h6" fontWeight={600}>
                RC Professionnelle
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Nom de l'assurance"
                  value={formData.rcProAssuranceNom}
                  onChange={handleChange('rcProAssuranceNom')}
                  placeholder="Ex: Allianz"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Numéro de police"
                  value={formData.rcProNumeroPolice}
                  onChange={handleChange('rcProNumeroPolice')}
                  placeholder="Ex: RC-2025-789012"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Date de validité"
                  type="date"
                  value={formData.rcProDateValidite}
                  onChange={handleChange('rcProDateValidite')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Section 3: Identification Juridique */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Receipt color="primary" />
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Identification Juridique
                </Typography>
                <Chip label="OBLIGATOIRE" color="error" size="small" sx={{ mt: 0.5 }} />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Forme juridique *"
                  value={formData.formeJuridique}
                  onChange={handleChange('formeJuridique')}
                  required
                >
                  {FORMES_JURIDIQUES.map((forme) => (
                    <MenuItem key={forme} value={forme}>
                      {forme}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Capital social"
                  value={formData.capitalSocial}
                  onChange={handleChange('capitalSocial')}
                  placeholder="Ex: Capital social : 10 000 €"
                  helperText="Pour les sociétés (SARL, SAS, etc.)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="RCS"
                  value={formData.rcs}
                  onChange={handleChange('rcs')}
                  placeholder="Ex: RCS Paris 123 456 789"
                  helperText="Pour les sociétés"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="RM (Répertoire des Métiers)"
                  value={formData.rm}
                  onChange={handleChange('rm')}
                  placeholder="Ex: RM Paris 123 456 789"
                  helperText="Pour les artisans"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Section 4: Conditions de Paiement */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Gavel color="primary" />
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Conditions de Paiement
                </Typography>
                <Chip label="OBLIGATOIRE" color="error" size="small" sx={{ mt: 0.5 }} />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Délai de paiement (jours) *"
                  type="number"
                  value={formData.delaiPaiementJours}
                  onChange={handleChange('delaiPaiementJours')}
                  required
                  helperText="Maximum 30 jours (Art. L.441-6)"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Taux de pénalités de retard (%) *"
                  type="number"
                  value={formData.tauxPenalitesRetard}
                  onChange={handleChange('tauxPenalitesRetard')}
                  required
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText="Légal: 12% (3 fois taux BCE)"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Indemnité forfaitaire (€) *"
                  type="number"
                  value={formData.indemniteForfaitaireRecouvrement}
                  onChange={handleChange('indemniteForfaitaireRecouvrement')}
                  required
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                  helperText="Légal: 40 € (Art. D.441-5)"
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Chip label="Escompte (optionnel)" size="small" />
                </Divider>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Taux d'escompte (%)"
                  type="number"
                  value={formData.tauxEscompte}
                  onChange={handleChange('tauxEscompte')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText="Réduction si paiement anticipé"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Délai pour escompte (jours)"
                  type="number"
                  value={formData.delaiEscompteJours}
                  onChange={handleChange('delaiEscompteJours')}
                  helperText="Ex: 8 jours pour 2% d'escompte"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Section 5: Coordonnées Bancaires */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AccountBalance color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Coordonnées Bancaires
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="IBAN"
                  value={formData.iban}
                  onChange={handleChange('iban')}
                  placeholder="FR76 1234 5678 9012 3456 7890 123"
                  error={!!formData.iban && formData.iban !== '' && !legalInfoService.validateIBAN(formData.iban)}
                  helperText={
                    !!formData.iban && formData.iban !== '' && !legalInfoService.validateIBAN(formData.iban)
                      ? 'IBAN invalide'
                      : 'Format: FR76 1234 5678 9012 3456 7890 123'
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="BIC"
                  value={formData.bic}
                  onChange={handleChange('bic')}
                  placeholder="BNPAFRPPXXX"
                  error={!!formData.bic && formData.bic !== '' && !legalInfoService.validateBIC(formData.bic)}
                  helperText={
                    !!formData.bic && formData.bic !== '' && !legalInfoService.validateBIC(formData.bic)
                      ? 'BIC invalide'
                      : '8 ou 11 caractères'
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Nom de la banque"
                  value={formData.nomBanque}
                  onChange={handleChange('nomBanque')}
                  placeholder="BNP Paribas"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Section 6: Qualifications */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <School color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Qualifications & Médiateur
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Qualification professionnelle"
                  value={formData.qualificationProfessionnelle}
                  onChange={handleChange('qualificationProfessionnelle')}
                  placeholder="Ex: Qualibat 2162 - Travaux de carrelage"
                  helperText="Qualibat, RGE, etc."
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Médiateur de la consommation *"
                  value={formData.mediateurConsommation}
                  onChange={handleChange('mediateurConsommation')}
                  required
                  helperText="Obligatoire depuis 2016 (Directive 2013/11/UE)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Site web du médiateur"
                  value={formData.mediateurSiteWeb}
                  onChange={handleChange('mediateurSiteWeb')}
                  placeholder="https://www.cnpm-mediation-consommation.eu"
                  type="url"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Section 7: TVA Spécifique */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Receipt color="primary" />
              <Typography variant="h6" fontWeight={600}>
                TVA Spécifique
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.franchiseTva}
                      onChange={handleChange('franchiseTva')}
                    />
                  }
                  label="Franchise en base de TVA (Art. 293 B du CGI)"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, ml: 4 }}>
                  Pour auto-entrepreneurs en franchise de TVA. Affichera la mention "TVA non applicable" sur les factures.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Agrément TVA réduite"
                  value={formData.agrementTvaReduite}
                  onChange={handleChange('agrementTvaReduite')}
                  placeholder="Ex: Agrément TVA réduite : AGR-2025-123"
                  helperText="Pour travaux avec TVA 5.5% ou 10%"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Actions */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/dashboard')}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveOutlined />}
            disabled={saving}
            sx={{
              bgcolor: '#10B981',
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default LegalInfoPage;
